// The playable sky as SVG with a viewBox in sky units (Tech Stack: SVG
// renderer): the sky fills the gameplay area's height and pans sideways;
// glyphs, lines and labels are drawn in sky units so they scale truly.
import {
  derive,
  DISC_FRACTION,
  enclosure,
  GLYPH_UNITS,
  letterValue,
  PLANET_UNITS,
  planetAppearance,
  planetPath,
  planetsOf,
  skyWidth,
  treeLines,
  type Constellation,
  type Planet,
  type PlayLevel,
  type PlayState,
} from '@wits/core';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { inCase, type Lettercase } from '../../systems/settings/gameSettings';
import { Icon } from '../components/Icon';
import { acceptTimeline, lineWaves, startIndex, waveCount } from './acceptMotion';
import { baselineDrop, CAP_HEIGHT } from './glyphInk';
import { longestPath } from './constellationPath';
import { useReducedMotion } from './reducedMotion';
import { boardMotion, nextBoardSession, quietZones } from './skyMotionStore';
import { platformOf } from '../components/usePlatform';
import { clampPan, computeViewport, glyphScale, hitRadius, initialPan, skyInsets, viewBoxOf, type SafeArea, type Viewport } from './viewport';

const GLYPH_R = GLYPH_UNITS / 2;
const DISC_R = GLYPH_R * DISC_FRACTION;
/** The hover and selected glow: a halo brightest at the disc's edge, fading out to this radius. */
const GLOW_R = DISC_R * 1.6;
/** A star's point value (Apogee), centred under its disc. */
const VALUE_SIZE = 26;
const VALUE_GAP = 5;
const GLOW_COLORS = { star: 'var(--star-white)' } as const;
const SPARK_COUNT = 8;
/** Two taps on the open sky this close in time and place clear the word in progress. */
const DOUBLE_TAP_MS = 350;
const DOUBLE_TAP_PX = 30;
/** The bright dash the shine runs along each line. */
const SHINE_UNITS = 70;
/** How wide a constellation's line takes a tap, px: a finger's width. */
const LINE_HIT_PX = 32;

interface Props {
  board: PlayState;
  showLetterValues: boolean;
  /** The case the stars' letters are drawn in (the Lettercase setting). */
  lettercase: Lettercase;
  /** The word just accepted; each acceptance has its own seq. */
  justAccepted: { word: string; seq: number } | null;
  onTapNode: (id: number) => void;
  onTapConstellation: (word: string) => void;
  onDoubleTapSky: () => void;
  /** A single tap on the open sky, away from the stars (it shows or quiets the HUD). */
  onTapSky: () => void;
  /** The accepted word's sequence has played (or was skipped). */
  onAcceptSettled: (seq: number) => void;
}

type Timing = { delay: number; duration: number };
const timing = (t: Timing): CSSProperties => ({ animationDelay: `${t.delay}ms`, animationDuration: `${t.duration}ms` });

export function SkyBoard({ board, showLetterValues, lettercase, justAccepted, onTapNode, onTapConstellation, onDoubleTapSky, onTapSky, onAcceptSettled }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const safeRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number; safe: SafeArea }>({ w: 0, h: 0, safe: NO_SAFE_AREA });
  const width = skyWidth(board.level.sky.preset);
  // The sky insets of the board's own size, so they follow the platform without waiting for it.
  const vp: Viewport = useMemo(() => computeViewport(size.w, size.h, width, skyInsets(platformOf(size.w, size.h), size.safe)), [size, width]);
  const [pan, setPan] = useState<number | null>(null);
  // Taps are resolved on pointer up (down and up on the same target without a
  // drag) rather than through click events: the board captures the pointer
  // for panning, which retargets the click away from the node.
  const drag = useRef<{ startX: number; pan0: number; moved: boolean; pointerId: number; target: TapTarget | null } | null>(null);
  const lastSkyTap = useRef<{ t: number; x: number; y: number } | null>(null);
  // While a word is in progress a sky tap waits out the double-tap window before it counts.
  const pendingSkyTap = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The node under a mouse or pen (touch has no hover) and the node held down.
  const [hover, setHover] = useState<number | null>(null);
  const [pressed, setPressed] = useState<number | null>(null);
  const reducedMotion = useReducedMotion();

  // The accepted word's sequence (acceptMotion.ts) plays from the first render
  // its constellation appears in, once per acceptance; a board that mounts
  // with a word already accepted shows it settled. Reduced motion skips it.
  const [settledSeq, setSettledSeq] = useState(justAccepted?.seq ?? 0);
  const pendingSeq = justAccepted && justAccepted.seq !== settledSeq ? justAccepted.seq : null;
  const accepting = pendingSeq !== null && !reducedMotion ? (board.constellations.find((c) => c.word === justAccepted!.word) ?? null) : null;
  // Its tree lines draw outward from a star picked for this acceptance; a
  // ringed planet's closing line follows as one last wave.
  const draws = useMemo(
    () => (accepting && pendingSeq !== null ? lineWaves(treeLines(accepting), accepting.nodes[startIndex(pendingSeq, accepting.word, accepting.nodes.length)]!) : null),
    [accepting, pendingSeq],
  );
  const tl = useMemo(
    () => (accepting && draws ? acceptTimeline(accepting.nodes.length, waveCount(draws), accepting.lines.length, accepting.planet !== undefined) : null),
    [accepting, draws],
  );
  const acceptTotal = tl?.total ?? 0;
  useEffect(() => {
    if (pendingSeq === null) return;
    const t = setTimeout(() => {
      setSettledSeq(pendingSeq);
      onAcceptSettled(pendingSeq);
    }, acceptTotal);
    return () => clearTimeout(t);
  }, [pendingSeq, acceptTotal, onAcceptSettled]);

  // A node that joins the selection pops with a burst of sparks. Its pop count
  // keys the glyph, so the same node pops again each time it is reselected.
  const [seenSelection, setSeenSelection] = useState(board.selection);
  const [pops, setPops] = useState<ReadonlyMap<number, number>>(() => new Map());
  if (board.selection !== seenSelection) {
    const added = board.selection.filter((id) => !seenSelection.includes(id));
    setSeenSelection(board.selection);
    if (added.length > 0 && !reducedMotion) {
      setPops((m) => new Map([...m, ...added.map((id): [number, number] => [id, (m.get(id) ?? 0) + 1])]));
    }
  }

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight, safe: safeAreaOf(safeRef.current) });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Start centered; keep the pan legal when the view changes.
  useEffect(() => {
    if (size.h === 0) return;
    setPan((p) => (p === null ? initialPan(vp) : clampPan(p, vp)));
  }, [vp, size.h]);

  const effectivePan = pan ?? initialPan(vp);

  // Tell the sky behind: the pan, for its parallax, and where the nodes and
  // the planet are, so nothing moves near them.
  const session = useMemo(nextBoardSession, []);
  const planets = planetsOf(board.level);
  useEffect(() => {
    if (size.h === 0) return;
    boardMotion.set({ session, pan: effectivePan, zones: quietZones([...board.level.nodes, ...planets], effectivePan, vp.scale, GLYPH_R, vp.offsetY) });
  }, [session, effectivePan, vp.scale, vp.offsetY, size.h, board.level.nodes, planets]);
  useEffect(() => () => boardMotion.set(null), []);

  const cancelSkyTap = useCallback(() => {
    if (pendingSkyTap.current !== null) clearTimeout(pendingSkyTap.current);
    pendingSkyTap.current = null;
  }, []);
  useEffect(() => cancelSkyTap, [cancelSkyTap]);

  /** Whether a point on the screen lies within a star's width of a star still free to take (a near miss). */
  const nearFreeStar = useCallback(
    (clientX: number, clientY: number) => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return false;
      const x = effectivePan + (clientX - rect.left) / vp.scale;
      const y = (clientY - rect.top) / vp.scale - vp.offsetY;
      const reach = 2 * hitRadius(vp.scale, GLYPH_R * glyphScale(vp.scale, GLYPH_UNITS));
      const locked = derive(board).locked;
      return board.level.nodes.some((n) => !locked.has(n.id) && Math.hypot(n.x - x, n.y - y) < reach);
    },
    [board, effectivePan, vp],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      // The edge chevrons are ordinary buttons: leave their pointer alone.
      if ((e.target as Element).closest?.('.pan-edge')) return;
      const target = tapTargetOf(e.target);
      drag.current = { startX: e.clientX, pan0: effectivePan, moved: false, pointerId: e.pointerId, target };
      setPressed(target?.kind === 'node' ? target.id : null);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [effectivePan],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      const d = drag.current;
      if (!d) {
        if (e.pointerType !== 'touch') setHover(nodeIdOf(e.target));
        return;
      }
      if (e.pointerId !== d.pointerId) return;
      const dxPx = e.clientX - d.startX;
      if (!d.moved && Math.abs(dxPx) < 6) return;
      if (!d.moved) {
        setPressed(null);
        setHover(null);
      }
      d.moved = true;
      setPan(clampPan(d.pan0 - dxPx / vp.scale, vp));
    },
    [vp],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      const d = drag.current;
      if (!d || e.pointerId !== d.pointerId) return;
      drag.current = null;
      setPressed(null);
      // The board held the pointer while it was down: find what it is over now.
      if (e.pointerType !== 'touch') setHover(nodeIdOf(document.elementFromPoint(e.clientX, e.clientY)));
      if (d.moved || e.type === 'pointercancel') {
        lastSkyTap.current = null;
        return;
      }
      if (!d.target) {
        // A tap on the open sky; a second one close behind it is a double tap.
        const last = lastSkyTap.current;
        if (last && e.timeStamp - last.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - last.x, e.clientY - last.y) < DOUBLE_TAP_PX) {
          lastSkyTap.current = null;
          cancelSkyTap();
          onDoubleTapSky();
          return;
        }
        lastSkyTap.current = { t: e.timeStamp, x: e.clientX, y: e.clientY };
        // A tap that just missed a star is no tap on the sky.
        if (nearFreeStar(e.clientX, e.clientY)) return;
        if (board.selection.length === 0) onTapSky();
        else {
          cancelSkyTap();
          pendingSkyTap.current = setTimeout(() => {
            pendingSkyTap.current = null;
            onTapSky();
          }, DOUBLE_TAP_MS);
        }
        return;
      }
      lastSkyTap.current = null;
      // A tap: the pointer went down and came up on the same node or label.
      if (tapTargetOf(e.target)?.key !== d.target.key && e.type !== 'pointerup') return;
      if (e.pointerType === 'touch') swallowClickAt(e.clientX, e.clientY);
      if (d.target.kind === 'node') onTapNode(d.target.id);
      else onTapConstellation(d.target.word);
    },
    [onTapNode, onTapConstellation, onDoubleTapSky, onTapSky, board.selection.length, nearFreeStar, cancelSkyTap],
  );

  const panBy = useCallback((units: number) => setPan((p) => clampPan((p ?? initialPan(vp)) + units, vp)), [vp]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') panBy(-200);
      if (e.key === 'ArrowRight') panBy(200);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panBy]);

  const onWheel = useCallback(
    (e: WheelEvent) => {
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (dx === 0) return;
      e.preventDefault();
      panBy(dx / vp.scale);
    },
    [panBy, vp.scale],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // A touch's taps are all settled on pointer up, so the click a phone sends
  // after it is not wanted: by then a tap on a star has brought the word panel
  // up under the finger, and the click would land on its Submit.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTouchEnd = (e: TouchEvent) => {
      if ((e.target as Element).closest?.('.pan-edge')) return;
      e.preventDefault();
    };
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => el.removeEventListener('touchend', onTouchEnd);
  }, []);

  const derived = useMemo(() => derive(board), [board]);
  // The ring the word in progress would make: previewed while it holds.
  const ring = useMemo(() => enclosure(board, derived), [board, derived]);
  const nodes = derived.nodes;
  const selectedIndex = new Map(board.selection.map((id, i) => [id, i]));
  const g = glyphScale(vp.scale, GLYPH_UNITS);
  const hit = hitRadius(vp.scale, GLYPH_R * g);
  const canWest = vp.pannable && effectivePan > vp.minPan + 1;
  const canEast = vp.pannable && effectivePan < vp.maxPan - 1;

  return (
    <div
      ref={ref}
      className={reducedMotion ? 'board reduced-motion' : 'board'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={() => setHover(null)}
    >
      {/* Measures the device's safe areas (the notch, the home indicator), which only CSS can read. */}
      <div ref={safeRef} className="board-safe-area" aria-hidden />
      {size.h > 0 && (
        <svg viewBox={viewBoxOf(effectivePan, vp)} preserveAspectRatio="xMidYMid slice">
          <defs>
            {Object.entries(GLOW_COLORS).map(([kind, color]) => (
              <GlowGradient key={kind} id={`glyph-glow-${kind}`} color={color} />
            ))}
          </defs>
          {/* Submitted constellations: lines, then label pills. The one just
              accepted also keeps its dashed lines a moment, and gets a shine.
              A tap on a line, like one on its pill, asks to remove it: an
              unseen band over each line takes it. */}
          {board.constellations.map((c) => {
            const arriving = c === accepting ? tl : null;
            const ends = c.lines.map(([a, b]) => [nodes.get(a)!, nodes.get(b)!] as const);
            return (
              <g key={c.word} className="constellation">
                {arriving &&
                  ends.map(([pa, pb], k) => (
                    <line key={`d${k}`} className="line-progress departing" style={timing(arriving.dashFade)} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} />
                  ))}
                {ends.map(([pa, pb], k) => {
                  const key = `${c.lines[k]![0]}-${c.lines[k]![1]}`;
                  // A ringed planet's closing line is the last one; it draws after the tree's waves.
                  const closing = c.planet !== undefined && k === c.lines.length - 1;
                  const draw = arriving && !closing ? draws?.[k] : undefined;
                  const when = arriving ? (closing ? arriving.closing : draw ? arriving.lineWave(draw.wave) : null) : null;
                  if (!arriving || !when) return <line key={key} className="line-locked" x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} />;
                  // Drawn from its outward end, as a dash that grows to the line's length.
                  const from = draw ? nodes.get(draw.from)! : pa;
                  const to = draw ? nodes.get(draw.to)! : pb;
                  const len = Math.hypot(to.x - from.x, to.y - from.y);
                  const style = { ...timing(when), strokeDasharray: `${len} ${len}`, '--line-length': len } as CSSProperties;
                  return <line key={key} className="line-locked drawing" style={style} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />;
                })}
                {arriving &&
                  ends.map(([pa, pb], k) => {
                    const len = Math.hypot(pb.x - pa.x, pb.y - pa.y);
                    const style = {
                      ...timing(arriving.shine(k)),
                      strokeDasharray: `${SHINE_UNITS} ${len + SHINE_UNITS}`,
                      '--shine-from': SHINE_UNITS,
                      '--shine-to': -len,
                    } as CSSProperties;
                    return <line key={`s${k}`} className="line-shine" style={style} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} />;
                  })}
                {ends.map(([pa, pb], k) => (
                  <line key={`h${k}`} className="line-hit" data-word={c.word} strokeWidth={LINE_HIT_PX / vp.scale} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} />
                ))}
              </g>
            );
          })}
          {/* The word in progress. */}
          {board.selectionLines.map(([a, b]) => {
            const pa = nodes.get(a)!;
            const pb = nodes.get(b)!;
            return <line key={`p${a}-${b}`} className="line-progress" x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} />;
          })}
          {/* The ring it would make round the planet: the loop filled in the planet's colour, and the closing line. */}
          {ring && (
            <g className="ring-preview" style={planetStyle(board.level, planets.find((p) => p.id === ring.planetId))}>
              <polygon className="planet-preview" points={ring.loop.map((id) => `${nodes.get(id)!.x},${nodes.get(id)!.y}`).join(' ')} />
              <line
                className="line-progress planet-closing"
                x1={nodes.get(ring.closing[0])!.x}
                y1={nodes.get(ring.closing[0])!.y}
                x2={nodes.get(ring.closing[1])!.x}
                y2={nodes.get(ring.closing[1])!.y}
              />
            </g>
          )}
          {/* The planets: each slashed while a line blocks it; the one a word has just ringed spins round once. */}
          {planets.map((planet) => {
            const activated = derived.planetsActivated.has(planet.id);
            const blocked = !activated && derived.planetsBlocked.has(planet.id);
            const spinning = tl && accepting?.planet?.id === planet.id ? tl.spin : null;
            const state = activated ? 'planet-activated' : blocked ? 'planet-blocked' : 'planet-free';
            const look = planetAppearance(board.level, planet);
            const slash = PLANET_UNITS * 0.35;
            return (
              <g
                key={`planet${planet.id}`}
                className={`planet ${state}`}
                style={planetStyle(board.level, planet)}
                transform={`translate(${planet.x} ${planet.y})`}
                aria-label={activated ? 'Planet, ringed' : 'Planet'}
              >
                <g transform={`scale(${g})`}>
                  {blocked && <line className="slash" x1={-slash} y1={slash} x2={slash} y2={-slash} />}
                  <path className={spinning ? 'planet-body spinning' : 'planet-body'} style={spinning ? timing(spinning) : undefined} d={planetPath(look)} />
                </g>
              </g>
            );
          })}
          {/* Nodes. */}
          {board.level.nodes.map((node) => {
            const locked = derived.locked.has(node.id);
            const selected = selectedIndex.has(node.id);
            const blocked = !locked && !selected && derived.blocked.has(node.id);
            const state = locked ? 'node-locked' : selected ? 'node-selected' : blocked ? 'node-blocked' : 'node-free';
            const glow = 'star';
            const label = inCase(node.letter, lettercase);
            // Its place in the word just accepted, while that word's sequence plays.
            const order = tl && accepting ? accepting.nodes.indexOf(node.id) : -1;
            const arrival = tl && order >= 0 ? tl : null;
            // Only a node a tap can select or deselect reacts to the pointer.
            const live = !locked && !blocked;
            const pointer = `${live && hover === node.id ? ' hover' : ''}${live && pressed === node.id ? ' pressed' : ''}`;
            const pop = pops.get(node.id) ?? 0;
            const popping = selected && pop > 0;
            const fontSize = 48;
            // Each letter centred on its own ink (glyphInk.ts), whatever its case.
            const baseline = fontSize * baselineDrop(label);
            return (
              <g
                key={node.id}
                className={`node ${state}${pointer}`}
                transform={`translate(${node.x} ${node.y})`}
                data-node={locked ? undefined : node.id}
                data-word={locked ? derived.locked.get(node.id) : undefined}
                role="button"
                aria-label={`Star ${node.letter}`}
              >
                <circle r={hit} fill="transparent" />
                <g transform={`scale(${g})`}>
                  {arrival && (
                    <g className="depart" style={timing(arrival.depart(order))}>
                      <circle className="glow-halo" r={GLOW_R} fill={`url(#glyph-glow-${glow})`} />
                      <circle className="disc" r={DISC_R} />
                      <text className="letter" textAnchor="middle" fontSize={fontSize} y={baseline}>
                        {label}
                      </text>
                    </g>
                  )}
                  {locked && (
                    <g className={arrival ? 'arrive' : undefined} style={arrival ? timing(arrival.arrive(order)) : undefined}>
                      <AtlasStar />
                    </g>
                  )}
                  {arrival && <Sparks key={`land${pendingSeq}`} from={20} to={[50, 44]} size={0.75} delay={arrival.sparks(order)} />}
                  {!locked && (
                    <>
                      <g key={`glyph${pop}`} className={popping ? 'glyph pop' : 'glyph'}>
                        <g className="glow">
                          <circle className="glow-halo" r={GLOW_R} fill={`url(#glyph-glow-${glow})`} />
                        </g>
                        <circle className="disc" r={DISC_R} />
                        {blocked && <line className="slash" x1={-DISC_R * 0.72} y1={DISC_R * 0.72} x2={DISC_R * 0.72} y2={-DISC_R * 0.72} />}
                        <text className="letter" textAnchor="middle" fontSize={fontSize} y={baseline}>
                          {label}
                        </text>
                        {showLetterValues && (
                          <text className="letter-value" textAnchor="middle" fontSize={VALUE_SIZE} y={DISC_R + VALUE_GAP + VALUE_SIZE * CAP_HEIGHT}>
                            {letterValue(board.level.lang, node.letter)}
                          </text>
                        )}
                      </g>
                      {popping && <Sparks key={`sparks${pop}`} />}
                    </>
                  )}
                </g>
              </g>
            );
          })}
          {board.constellations.map((c) => (
            <LabelPill key={`l${c.word}`} constellation={c} nodes={nodes} arriving={c === accepting && tl ? tl.pill : null} />
          ))}
        </svg>
      )}
      {canWest && (
        <div className="pan-edge pan-edge-west">
          <button type="button" aria-label="Pan west" onClick={() => panBy(-vp.visibleWidth * 0.6)}>
            <Icon name="chevron-left" />
          </button>
        </div>
      )}
      {canEast && (
        <div className="pan-edge pan-edge-east">
          <button type="button" aria-label="Pan east" onClick={() => panBy(vp.visibleWidth * 0.6)}>
            <Icon name="chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}

type TapTarget = { kind: 'node'; id: number; key: string } | { kind: 'word'; word: string; key: string };

/** How soon after a touch tap, and how near it, a click is taken for that tap's own. */
const GHOST_CLICK_MS = 400;
const GHOST_CLICK_PX = 30;

/**
 * Drops the click a phone may still send after a touch tap the board has
 * settled: a tap on a star brings the word panel up under the finger, and
 * that click would land on its Submit. The touchend listener cancels it where
 * it can; this catches the rest.
 */
/** A planet's colour as the --planet the board's planet rules draw with (tokens.css --planet-<kind>). */
function planetStyle(level: PlayLevel, planet: Planet | undefined): CSSProperties | undefined {
  return planet ? ({ '--planet': `var(--planet-${planetAppearance(level, planet).kind})` } as CSSProperties) : undefined;
}

function swallowClickAt(x: number, y: number) {
  const until = performance.now() + GHOST_CLICK_MS;
  const swallow = (e: MouseEvent) => {
    window.removeEventListener('click', swallow, true);
    if (performance.now() > until || Math.hypot(e.clientX - x, e.clientY - y) > GHOST_CLICK_PX) return;
    e.preventDefault();
    e.stopPropagation();
  };
  window.addEventListener('click', swallow, true);
  setTimeout(() => window.removeEventListener('click', swallow, true), GHOST_CLICK_MS);
}

const NO_SAFE_AREA: SafeArea = { top: 0, right: 0, bottom: 0, left: 0 };

/** The safe areas as the probe's padding resolves them. */
function safeAreaOf(probe: HTMLElement | null): SafeArea {
  if (!probe) return NO_SAFE_AREA;
  const css = getComputedStyle(probe);
  const px = (v: string) => Number.parseFloat(v) || 0;
  return { top: px(css.paddingTop), right: px(css.paddingRight), bottom: px(css.paddingBottom), left: px(css.paddingLeft) };
}

function nodeIdOf(target: EventTarget | null): number | null {
  const t = tapTargetOf(target);
  return t?.kind === 'node' ? t.id : null;
}

function tapTargetOf(target: EventTarget | null): TapTarget | null {
  const el = (target as Element | null)?.closest?.('[data-node],[data-word]') as HTMLElement | SVGElement | null;
  if (!el) return null;
  const node = el.getAttribute('data-node');
  if (node !== null) return { kind: 'node', id: Number(node), key: `n${node}` };
  const word = el.getAttribute('data-word');
  if (word !== null) return { kind: 'word', word, key: `w${word}` };
  return null;
}

/** The atlas star symbol a locked star becomes: a four-point star with slim points. */
function AtlasStar() {
  return <path className="atlas-star" d={fourPointStar(26, 4.5)} />;
}

/** The halo behind a hovered or selected disc: clear inside, brightest at the disc's edge, gone by GLOW_R. */
function GlowGradient({ id, color }: { id: string; color: string }) {
  const edge = DISC_R / GLOW_R;
  return (
    <radialGradient id={id}>
      <stop offset={edge * 0.85} style={{ stopColor: color, stopOpacity: 0 }} />
      <stop offset={edge} style={{ stopColor: color, stopOpacity: 0.5 }} />
      <stop offset={(edge + 1) / 2} style={{ stopColor: color, stopOpacity: 0.16 }} />
      <stop offset={1} style={{ stopColor: color, stopOpacity: 0 }} />
    </radialGradient>
  );
}

/**
 * A burst of dots and tiny four-point stars flung out from `from` to `to`
 * (dots, stars) units: as a node is selected, from its disc's edge, and as
 * an accepted word's star lands, smaller and after `delay` ms.
 */
function Sparks({
  from = DISC_R * 1.15,
  to = [DISC_R * 2.1, DISC_R * 1.85],
  size = 1,
  delay = 0,
}: {
  from?: number;
  to?: [number, number];
  size?: number;
  delay?: number;
}) {
  return (
    <g className="sparks">
      {Array.from({ length: SPARK_COUNT }, (_, i) => {
        const odd = i % 2 === 1;
        const angle = ((i + (odd ? 0.2 : 0)) / SPARK_COUNT) * 2 * Math.PI - Math.PI / 2;
        const reach = odd ? to[1] : to[0];
        const style = {
          '--x0': `${Math.cos(angle) * from}px`,
          '--y0': `${Math.sin(angle) * from}px`,
          '--x1': `${Math.cos(angle) * reach}px`,
          '--y1': `${Math.sin(angle) * reach}px`,
          animationDelay: delay ? `${delay}ms` : undefined,
        } as CSSProperties;
        return (
          <g key={i} className="spark" style={style}>
            {odd ? <path d={fourPointStar(10 * size, 2.2 * size)} /> : <circle r={3.5 * size} />}
          </g>
        );
      })}
    </g>
  );
}

/** A four-point star path, `o` to the tips and `i` to the inner corners. */
function fourPointStar(o: number, i: number): string {
  return `M0 ${-o} L${i} ${-i} L${o} 0 L${i} ${i} L0 ${o} L${-i} ${i} L${-o} 0 L${-i} ${-i} Z`;
}

function LabelPill({
  constellation,
  nodes,
  arriving,
}: {
  constellation: Constellation;
  nodes: Map<number, { x: number; y: number }>;
  /** The word fades in with a small bounce as its constellation is accepted. */
  arriving: Timing | null;
}) {
  const path = longestPath(constellation.nodes, treeLines(constellation));
  let x: number;
  let y: number;
  if (path.length >= 2) {
    const mid = Math.floor((path.length - 2) / 2);
    const a = nodes.get(path[mid]!)!;
    const b = nodes.get(path[mid + 1]!)!;
    x = (a.x + b.x) / 2;
    y = (a.y + b.y) / 2;
  } else {
    const n = nodes.get(constellation.nodes[0]!)!;
    x = n.x;
    y = n.y + 70;
  }
  const label = constellation.word;
  const width = label.length * 10.5 + 22;
  const height = 24;
  return (
    <g className="label-pill" transform={`translate(${x} ${y})`} data-word={label} role="button" aria-label={`Constellation ${label}`}>
      <g className={arriving ? 'pill-body arriving' : 'pill-body'} style={arriving ? timing(arriving) : undefined}>
        <rect x={-width / 2} y={-height / 2} width={width} height={height} rx={height / 2} />
        <text textAnchor="middle" dominantBaseline="central" fontSize={12.5} y={0.5}>
          {label}
        </text>
      </g>
    </g>
  );
}
